import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import OriginDataService from '../../services/origin.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const OriginList = () => {
  const [origins, setOrigins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchOrigins();
  }, []);

  const fetchOrigins = async () => {
    setLoading(true);
    try {
      const response = await OriginDataService.getAll({ search: searchTerm });
      setOrigins(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching origins:', error);
      showSnackbar(t('origins.errorLoadingOrigins') + ': ' + (error.response?.data?.message || error.message), 'error');
      setOrigins([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchOrigins();
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await OriginDataService.delete(deleteDialog.id);
      showSnackbar(t('origins.originDeletedSuccess'));
      fetchOrigins();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error deleting origin';
      showSnackbar(errorMessage, 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const toggleActive = async (origin) => {
    try {
      await OriginDataService.update(origin.id, {
        ...origin,
        is_active: !origin.is_active
      });
      showSnackbar(t('origins.originUpdatedSuccess'));
      fetchOrigins();
    } catch (error) {
      showSnackbar(t('origins.errorUpdatingOrigin'), 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getName = (origin) => {
    return language === 'es' ? origin.name_es : origin.name_en;
  };

  const getDescription = (origin) => {
    return language === 'es' ? origin.description_es : origin.description_en;
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('origins.title')}</Typography>
            {hasPermission('origins.new') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/origins/new')}
              >
                {t('origins.newOrigin')}
              </Button>
            )}
          </Box>

          <TextField
            placeholder={t('origins.searchOrigins')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('origins.name')} (EN)</TableCell>
                  <TableCell>{t('origins.name')} (ES)</TableCell>
                  <TableCell>{t('origins.code')}</TableCell>
                  <TableCell>{t('origins.description')}</TableCell>
                  <TableCell>{t('origins.status')}</TableCell>
                  <TableCell align="center">{t('origins.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : origins.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t('origins.noOriginsFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  origins.map((origin) => (
                    <TableRow key={origin.id} hover>
                      <TableCell>{origin.name_en}</TableCell>
                      <TableCell>{origin.name_es}</TableCell>
                      <TableCell>
                        {origin.code ? (
                          <Chip label={origin.code} size="small" variant="outlined" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {getDescription(origin) || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={origin.is_active}
                              onChange={() => toggleActive(origin)}
                              size="small"
                            />
                          }
                          label={origin.is_active ? 'Active' : 'Inactive'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/origins/${origin.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(origin.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>Delete Origin</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this origin?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OriginList;
